import { GeocodeOptions, OutputFormat, RequestType, geocode, setDefaults } from "react-geocode";

import { getGoogleKey } from "utils";

import { COUNTRY_CODE, SupportedCountryPostcodes, ZIP_CODE_COUNT_BY_COUNTRY } from "consts";

import { CropSaleData, RetailerPurchase } from "models";

enum ADDRESS_COMPONENT_TYPES {
  ADMINISTRATIVE_AREA_LEVEL_1 = "administrative_area_level_1",
  POSTAL_CODE = "postal_code",
  COUNTRY = "country",
  POLITICAL = "political",
}
interface AddressComponent {
  long_name?: string;
  short_name?: string;
  types: ADDRESS_COMPONENT_TYPES[];
}

export const isUniqueRetailerName = (purchases: RetailerPurchase[], name: string, currentIndex: number): boolean =>
  purchases.every((purchase, index) => index === currentIndex || purchase.retailer.name !== name);

export const isUniquePurchaserName = (cropSales: CropSaleData[], name: string, currentIndex: number): boolean =>
  cropSales.every((sale, index) => index === currentIndex || sale.purchaser.name !== name);

export const isValidTotalPurchasePercentage = (
  purchases: RetailerPurchase[],
  field: keyof Omit<RetailerPurchase, "retailer" | "id" | "retailerId">,
  value: number,
  currentIndex: number
): boolean => {
  const otherPurchasesTotal = purchases.reduce((sum, purchase, index) => {
    if (index !== currentIndex) {
      return sum + (Number(purchase[field]) ?? 0);
    }
    return sum;
  }, 0);

  const totalPercentage = otherPurchasesTotal * 100 + value;
  return totalPercentage <= 100;
};

export const isValidTotalCropSalePercentage = (cropSales: CropSaleData[], value: number, currentIndex: number): boolean => {
  const otherCropsSalesTotal = cropSales.reduce((sum, purchase, index) => {
    if (index !== currentIndex) {
      return sum + (Number(purchase.soldYieldPct) ?? 0);
    }
    return sum;
  }, 0);

  return otherCropsSalesTotal * 100 + value === 100;
};

export const isValidPercentageDecimal = (v: number) => v >= 0 && v <= 100;

export const isValidNonnegativeNumber = (v: number) => v >= 0;

export const isNullOrUndefined = (v: unknown) => v === undefined || v === null;

export const isWithinBounds = (v: number, min: number, max: number) => v >= min && v <= max;

let isGeocodeInitialized = false;

export const initializeGeocodeDefaults = (country?: string) => {
  if (isGeocodeInitialized) return;
  isGeocodeInitialized = true;

  setDefaults({
    key: getGoogleKey(),
    language: country ?? COUNTRY_CODE.GERMANY,
    region: country ?? COUNTRY_CODE.GERMANY,
    outputFormat: OutputFormat.JSON,
  });
};

export const searchZipOverGoogleMapsAPI = async ({ country, zipCode }: { country: string; zipCode: string }) => {
  try {
    const options: GeocodeOptions = {
      components: `country:${country}`,
      outputFormat: OutputFormat.JSON,
    };

    const response = await geocode(RequestType.ADDRESS, zipCode, options);
    if (response.status !== "OK") return null;

    const foundRegion = response.results[0].address_components.find(
      (component: AddressComponent) => component.types.indexOf(ADDRESS_COMPONENT_TYPES.ADMINISTRATIVE_AREA_LEVEL_1) >= 0
    )?.long_name;
    if (foundRegion) return foundRegion;

    const foundCountry = response.results[0].address_components.find(
      (component: AddressComponent) => component.types.includes(ADDRESS_COMPONENT_TYPES.COUNTRY) && component.types.includes(ADDRESS_COMPONENT_TYPES.POLITICAL)
    )?.short_name;
    const isPartialMatch = response.results[0].partial_match;

    return !isPartialMatch && foundCountry?.toLowerCase() === country.toLowerCase() ? foundCountry : null;
  } catch {
    return null;
  }
};

export const searchZip = async (zipCode: string, country: string) => {
  initializeGeocodeDefaults(country);
  if (!(country in ZIP_CODE_COUNT_BY_COUNTRY)) return null;

  const zipCodeCountByCountry = ZIP_CODE_COUNT_BY_COUNTRY[country as keyof typeof ZIP_CODE_COUNT_BY_COUNTRY];
  if (zipCode?.length !== zipCodeCountByCountry) return null;

  const regionFromGoogleMapsAPI = await searchZipOverGoogleMapsAPI({ country, zipCode });
  return regionFromGoogleMapsAPI;
};

const POSTCODE_PATTERNS: Record<SupportedCountryPostcodes, RegExp> = {
  DE: /^\d{5}$/, // Germany: 5 digits (e.g., 10115)
  AT: /^\d{4}$/, // Austria: 4 digits (e.g., 2000)
  CH: /^\d{4}$/, // Switzerland: 4 digits (e.g., 8001)
  PL: /^\d{2}-\d{3}$/, // Poland: XX-XXX format (e.g., 00-950)
  US: /^\d{5}(-\d{4})?$/, // USA: 5 digits or ZIP+4 (e.g., 12345 or 12345-6789)
  GB: /^[A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2}$/i, // GB: Various formats (e.g., SW1A 1AA, M1 1AA)
};

export const isValidPostCode = (value: string, country: string | COUNTRY_CODE): boolean => {
  if (!value || !country) {
    return false;
  }

  const countryCode = country.toString().toUpperCase() as SupportedCountryPostcodes;

  const pattern = POSTCODE_PATTERNS[countryCode];

  if (!pattern) {
    return false;
  }

  return pattern.test(value.trim());
};
